import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, message } from 'antd';

import { useFilterOptionsOptional } from '../../../contexts/FilterOptionsContext';
import {
  getQuickInferenceFrameViewValidation,
  updateQuickInferenceFrameUserViews,
} from '../../../helpers/api/quick-inference/quick-inference';
import type {
  QuickInferenceFrame,
  QuickInferenceViewValidation,
} from '../../../helpers/api/quick-inference/quick-inference';
import { formatViewLabel } from './quickInferenceUtils';

interface Props {
  inferenceId: string;
  frameKey: string;
  frame: QuickInferenceFrame;
  editable: boolean;
  activeViewDefId: number | null;
  onActiveViewDefIdChange: (viewDefId: number | null) => void;
  onUpdated: () => void | Promise<void>;
}

export function QuickInferenceAssignViewBar({
  inferenceId,
  frameKey,
  frame,
  editable,
  activeViewDefId,
  onActiveViewDefIdChange,
  onUpdated,
}: Props) {
  const filterOptions = useFilterOptionsOptional();
  const viewsCatalog = filterOptions?.views ?? [];
  const [validation, setValidation] = useState<QuickInferenceViewValidation | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [busy, setBusy] = useState(false);

  const autoViews = frame.views || [];
  const userViews = frame.user_views || [];
  const pendingIds = frame.pending_user_views || [];

  const codeToDef = useMemo(() => {
    const map = new Map<string, { id: number; view_code: string; display_name: string }>();
    for (const v of viewsCatalog) {
      map.set(v.view_code, v);
    }
    return map;
  }, [viewsCatalog]);

  const idToDef = useMemo(() => {
    const map = new Map<number, { id: number; view_code: string; display_name: string }>();
    for (const v of viewsCatalog) {
      map.set(v.id, v);
    }
    return map;
  }, [viewsCatalog]);

  const displayName = useCallback(
    (viewDefId: number | null | undefined, fallbackCode?: string) => {
      if (viewDefId != null) {
        const def = idToDef.get(viewDefId);
        if (def) return def.display_name || formatViewLabel(def.view_code);
      }
      if (fallbackCode) return formatViewLabel(fallbackCode);
      return 'View';
    },
    [idToDef]
  );

  const refreshValidation = useCallback(
    async (viewDefId: number) => {
      setIsValidating(true);
      try {
        const result = await getQuickInferenceFrameViewValidation(
          inferenceId,
          frameKey,
          viewDefId
        );
        setValidation(result);
      } catch {
        setValidation(null);
      } finally {
        setIsValidating(false);
      }
    },
    [inferenceId, frameKey]
  );

  useEffect(() => {
    if (activeViewDefId == null) {
      setValidation(null);
      return;
    }
    void refreshValidation(activeViewDefId);
  }, [
    activeViewDefId,
    refreshValidation,
    frame.segmentations,
    frame.views,
    frame.user_views,
    frame.pending_user_views,
  ]);

  // Resume first pending when frame loads / changes and nothing active.
  useEffect(() => {
    if (activeViewDefId != null) return;
    if (pendingIds.length > 0) {
      onActiveViewDefIdChange(pendingIds[0]);
    }
  }, [activeViewDefId, pendingIds, onActiveViewDefIdChange]);

  const confirmedCodes = userViews;

  const handleSelectPending = useCallback(
    async (viewDefId: number) => {
      if (!editable) return;
      onActiveViewDefIdChange(viewDefId);
      if (pendingIds.includes(viewDefId) || userViews.includes(idToDef.get(viewDefId)?.view_code || '')) {
        return;
      }
      setBusy(true);
      try {
        await updateQuickInferenceFrameUserViews(inferenceId, frameKey, {
          action: 'pending',
          view_def_id: viewDefId,
        });
        await onUpdated();
      } catch (err: any) {
        message.error(err?.detail || err?.message || 'Failed to start assign');
      } finally {
        setBusy(false);
      }
    },
    [
      editable,
      onActiveViewDefIdChange,
      pendingIds,
      userViews,
      idToDef,
      inferenceId,
      frameKey,
      onUpdated,
    ]
  );

  const handleConfirm = useCallback(async () => {
    if (activeViewDefId == null || !editable) return;
    setBusy(true);
    try {
      await updateQuickInferenceFrameUserViews(inferenceId, frameKey, {
        action: 'confirm',
        view_def_id: activeViewDefId,
      });
      message.success('View confirmed');
      onActiveViewDefIdChange(null);
      await onUpdated();
    } catch (err: any) {
      message.error(err?.detail || err?.message || 'Cannot confirm view yet');
    } finally {
      setBusy(false);
    }
  }, [activeViewDefId, editable, inferenceId, frameKey, onActiveViewDefIdChange, onUpdated]);

  const handleRemove = useCallback(
    async (viewDefId: number) => {
      if (!editable) return;
      setBusy(true);
      try {
        await updateQuickInferenceFrameUserViews(inferenceId, frameKey, {
          action: 'remove',
          view_def_id: viewDefId,
        });
        if (activeViewDefId === viewDefId) {
          onActiveViewDefIdChange(null);
        }
        message.success('View removed');
        await onUpdated();
      } catch (err: any) {
        message.error(err?.detail || err?.message || 'Failed to remove view');
      } finally {
        setBusy(false);
      }
    },
    [editable, inferenceId, frameKey, activeViewDefId, onActiveViewDefIdChange, onUpdated]
  );

  const requiredChecks = (validation?.checks || []).filter((c) => c.required_for_match);
  const satisfied = requiredChecks.filter((c) => c.is_satisfied).length;
  const total = requiredChecks.length;
  const canConfirm = Boolean(validation?.all_required_satisfied);

  const selectOptions = viewsCatalog
    .filter((v) => !userViews.includes(v.view_code))
    .map((v) => ({
      value: v.id,
      label: v.display_name || formatViewLabel(v.view_code),
    }));

  return (
    <div className="shrink-0 border-b border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:bg-gray-700">
          Assign
        </span>

        {autoViews.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[10px] text-gray-400">AI</span>
            {autoViews.map((code) => {
              const def = codeToDef.get(code);
              return (
                <span
                  key={`ai-${code}`}
                  className="inline-flex h-6 items-center rounded-full border border-purple-200 bg-purple-50 px-2 text-[10px] font-medium text-purple-700"
                >
                  {displayName(def?.id, code)}
                </span>
              );
            })}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] text-gray-400">You</span>
          {confirmedCodes.length === 0 && pendingIds.length === 0 ? (
            <span className="text-[10px] italic text-gray-400">Not confirmed</span>
          ) : null}
          {confirmedCodes.map((code) => {
            const def = codeToDef.get(code);
            const vid = def?.id;
            return (
              <span
                key={`you-${code}`}
                className={`inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[10px] font-medium ${
                  vid != null && activeViewDefId === vid
                    ? 'border-blue-400 bg-blue-50 text-blue-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
              >
                <button
                  type="button"
                  disabled={!editable || vid == null}
                  onClick={() => vid != null && onActiveViewDefIdChange(vid)}
                  className="max-w-[8rem] truncate"
                >
                  {displayName(vid, code)}
                  <span className="ml-0.5 text-blue-400">✓</span>
                </button>
                {editable && vid != null ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleRemove(vid)}
                    className="text-gray-400 hover:text-red-500"
                    title="Remove confirmed view"
                  >
                    ×
                  </button>
                ) : null}
              </span>
            );
          })}
          {pendingIds.map((vid) => {
            const def = idToDef.get(vid);
            if (def && userViews.includes(def.view_code)) return null;
            return (
              <span
                key={`pending-${vid}`}
                className={`inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[10px] font-medium ${
                  activeViewDefId === vid
                    ? 'border-orange-400 bg-orange-50 text-orange-800'
                    : 'border-dashed border-orange-300 bg-orange-50 text-orange-700'
                }`}
              >
                <button
                  type="button"
                  disabled={!editable}
                  onClick={() => onActiveViewDefIdChange(vid)}
                  className="max-w-[8rem] truncate"
                >
                  {displayName(vid, def?.view_code)}
                  <span className="ml-0.5 opacity-70">…</span>
                </button>
                {editable ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleRemove(vid)}
                    className="text-gray-400 hover:text-red-500"
                    title="Cancel assign"
                  >
                    ×
                  </button>
                ) : null}
              </span>
            );
          })}
        </div>

        <Select
          value={undefined}
          disabled={!editable || busy || selectOptions.length === 0}
          placeholder="+ assign view…"
          options={selectOptions}
          onChange={(value) => {
            if (value != null) void handleSelectPending(Number(value));
          }}
          className="!w-full max-w-[10rem] sm:!w-40"
          size="small"
          showSearch
          optionFilterProp="label"
        />

        {(busy || isValidating) && (
          <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-blue-200 border-t-blue-500" />
        )}
      </div>

      {activeViewDefId != null ? (
        <div
          className={`border-t px-3 py-2.5 ${
            canConfirm
              ? 'border-emerald-100 bg-emerald-50/60 dark:border-emerald-900/30 dark:bg-emerald-900/10'
              : 'border-slate-100 bg-white dark:border-gray-700 dark:bg-gray-800/50'
          }`}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="shrink-0 text-[10px] font-semibold text-gray-500">
                Reviewing
              </span>
              <span className="truncate text-[10px] font-bold text-purple-700">
                {displayName(activeViewDefId)}
              </span>
              {!isValidating && total > 0 ? (
                <span
                  className={`inline-flex h-4 shrink-0 items-center rounded-full px-1.5 text-[9px] font-bold ${
                    satisfied === total
                      ? 'border border-green-200 bg-green-100 text-green-700'
                      : 'border border-orange-200 bg-orange-100 text-orange-700'
                  }`}
                >
                  {satisfied}/{total}
                </span>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                disabled={!editable || busy || isValidating || !canConfirm}
                onClick={() => void handleConfirm()}
                title={
                  canConfirm
                    ? 'Confirm view'
                    : 'Draw all required structures first'
                }
                className="h-6 rounded-lg bg-green-500 px-2.5 text-[10px] font-semibold text-white shadow-sm hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={() => onActiveViewDefIdChange(null)}
                className="flex h-6 w-6 items-center justify-center rounded-lg border border-gray-200 text-[11px] text-gray-400 hover:border-red-200 hover:text-red-400"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {isValidating ? (
            <p className="text-[10px] italic text-gray-400">Checking structures…</p>
          ) : requiredChecks.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {requiredChecks.map((check) => (
                <span
                  key={check.category_id}
                  className={`inline-flex h-6 items-center gap-1 rounded-lg border px-2 text-[10px] font-medium ${
                    check.is_satisfied
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-dashed border-orange-300 bg-white text-orange-600'
                  }`}
                >
                  <span
                    className={`text-[9px] font-bold ${
                      check.is_satisfied ? 'text-green-500' : 'text-orange-400'
                    }`}
                  >
                    {check.is_satisfied ? '✓' : '○'}
                  </span>
                  {check.display_name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[10px] italic text-gray-400">No required structures for this view.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
